import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LocalStorage from 'utils/myLocalStorage'
import { DisplayModus } from 'constants/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faPen } from '@fortawesome/free-solid-svg-icons'
import {
  Collapsible,
  IconButton,
  Inline,
  Spinner,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@airbus/components-react/components'

import ModalManHour from './modalManHour'
import { ManHour, ManHourInput } from 'types/workorder/manHour'
import { format } from 'date-fns'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import { Lang } from 'context/LangContext'
import URL_APIS from 'utils/api/urlApis'
import useMutation from 'utils/hooks/useMutation'
import ModalConfirm from 'components/Modal/ModalConfim'
import ComponentsUNames from 'constants/componentsUNames'
import useQuery from 'utils/hooks/useQuery'
import urlApis from 'utils/api/urlApis'
import ResponsiveTableCell from 'components/responsiveTableCell'

type Props = {
  keyForReload: string
  lang: Lang
  oenumdoc: string | null
  oeType: string | undefined
  applicability: string
  displayMode: DisplayModus
  expandAll: boolean
}

type SelectedManHour = ManHour & {
  checked?: boolean
  open?: boolean
  loading?: boolean
  errorMsg?: string
  done?: boolean
  date?: Date
  resourceType?: string
}

const WoManhour = ({ keyForReload, lang, oenumdoc, oeType, applicability, displayMode, expandAll }: Props) => {
  const { t } = useTranslation()

  const [isModalFormOpen, setIsModalFormOpen] = useState<boolean>(false)
  const [modalFormDisplayMode, setModalFormDisplayMode] = useState(DisplayModus.READ)
  const [selectedResource, setSelectedResource] = useState<ManHourInput>()

  const [data, setData] = useState<SelectedManHour[]>()
  const { fetchData: fetchManHours } = useQuery<ManHour[]>(urlApis.URL_WORKORDER_MANHOUR_GET)

  // Expand/collapse status of main component
  const [isExpanded, setIsExpanded] = useState(false)
  // Expand/collapse status of grouped-by type tables
  const [isExpandedGroupedByType, setIsExpandedGroupedByType] = useState({})
  const [manHoursToDelete, setManHoursToDelete] = useState<SelectedManHour | null>(null)

  const { mutate: deleteManHours } = useMutation(URL_APIS.URL_WORKORDER_MANHOUR_DELETE)

  const hasUserPermissions_StaffDisplay =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.GLOBAL_MANHOUR_DSP_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_DISPLAY_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    )
  const canCreate =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.GLOBAL_MANHOUR_DSP_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    (oeType !== 'DOC' ||
      (oeType === 'DOC' &&
        LocalStorage.getOrDefault(
          [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_OE_SB_PERMISSIONS],
          LocalStorage.VALUE_TYPE.BOOLEAN,
          false,
        ))) &&
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, 'AMSWEB_ACCESS_MOMO-CRT'],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    )
  const canUpdate =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.GLOBAL_MANHOUR_DSP_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    (oeType !== 'DOC' ||
      (oeType === 'DOC' &&
        LocalStorage.getOrDefault(
          [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_OE_SB_PERMISSIONS],
          LocalStorage.VALUE_TYPE.BOOLEAN,
          false,
        )))

  const handleModalFormPostSubmit = () => {
    setIsModalFormOpen(false)
    setSelectedResource(undefined)

    loadData()
  }

  const handleDelete = async () => {
    if (!manHoursToDelete) return
    setManHoursToDelete({ ...manHoursToDelete, loading: true, open: false })
    await deleteManHours({
      params: { id: manHoursToDelete.id.toString() },
      method: 'DELETE',
    })

    setManHoursToDelete(null)
    loadData()
  }

  const handleCreateManHour = () => {
    setSelectedResource({
      appl: applicability,
      numdoc: oenumdoc as string,
      date: new Date(),
      mohdep: format(new Date(), 'HH:mm'),
      datefin: new Date(),
      hfin: format(new Date(), 'HH:mm'),
      user: LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined),
      time: '00:00',
      qualification: undefined,
    })
    setModalFormDisplayMode(DisplayModus.CREATE)
    setIsModalFormOpen(true)
  }

  const handleUpdateManHour = (moToUpdate: SelectedManHour) => {
    const startDate = moToUpdate.date && new Date(moToUpdate.date[0], moToUpdate.date[1], moToUpdate.date[2])
    const startTime =
      moToUpdate.startTime &&
      new Date(
        moToUpdate.date[0],
        moToUpdate.date[1],
        moToUpdate.date[2],
        parseInt(moToUpdate.startTime.substring(0, moToUpdate.startTime.length - 2)),
        parseInt(moToUpdate.startTime.substring(moToUpdate.startTime.length - 2)),
      )
    const endTime =
      moToUpdate.endTime &&
      new Date(
        moToUpdate.date[0],
        moToUpdate.date[1],
        moToUpdate.date[2],
        parseInt(moToUpdate.endTime.substring(0, moToUpdate.endTime.length - 2)),
        parseInt(moToUpdate.endTime.substring(moToUpdate.endTime.length - 2)),
      )
    setSelectedResource({
      id: moToUpdate.id,
      numdoc: oenumdoc as string,
      appl: moToUpdate.appl,
      date: startDate,
      mohdep: startTime ? format(startTime, 'HH:mm') : '',
      datefin: startDate,
      hfin: endTime ? format(endTime, 'HH:mm') : '',
      time: moToUpdate.temps,
      user: moToUpdate.executant,
      qualification: moToUpdate.codeQualification,
    })
    setModalFormDisplayMode(DisplayModus.WRITE)
    setIsModalFormOpen(true)
  }

  const loadData = async () => {
    if (oenumdoc) {
      const response = await fetchManHours({
        lang: lang,
        oenumdoc: oenumdoc,
      })
      setData(response?.data?.content)
    }
  }

  /**
   * Load man-hours of Work-order
   */
  useEffect(() => {
    if (isExpanded) {
      loadData()
    } else {
      setData([])
    }
  }, [lang, isExpanded])

  /**
   * Load Man-Hour of Work-order
   */
  useEffect(() => {
    if (hasUserPermissions_StaffDisplay && isExpanded) {
      loadData()
    }
  }, [keyForReload, lang, isExpanded, hasUserPermissions_StaffDisplay])

  /**
   * ExpandAll and collapseAll are mirror-toggled, so a hook on expandAll is enough
   */
  useEffect(() => {
    setIsExpanded(expandAll)

    if (JSON.stringify(isExpandedGroupedByType) !== '{}') {
      const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
      Object.keys(copyIsExpandedGroupedByType).map((it) => (copyIsExpandedGroupedByType[it] = expandAll))
      setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
    }
  }, [expandAll])

  const formatStartTime = (sT: string) => {
    // sometimes backend returns date string with :
    if (sT?.includes(':')) return ' - ' + sT

    return sT ? ' - ' + ('0' + sT.substring(0, sT.length - 2)).slice(-2) + ':' + sT.substring(sT.length - 2) : ''
  }

  return hasUserPermissions_StaffDisplay ? (
    <div style={{ width: '100%' }}>
      <Collapsible title={t('8000113')} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <div>
          {applicability && canCreate && (
            <IconButton variant='ghost' size='small' onClick={handleCreateManHour} style={{ marginBottom: '1em' }}>
              <FontAwesomeIcon icon={faSquarePlus} />
            </IconButton>
          )}
        </div>
        <div>
          {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' ? (
            <div>
              <Table>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '5%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <ResponsiveTableCell>{t('9500034')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('9100287')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7000914')}</ResponsiveTableCell>
                    <ResponsiveTableCell align='right'>{t('7000027')}</ResponsiveTableCell>
                    <ResponsiveTableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data ? (
                    data.map((d: SelectedManHour, i: number) => (
                      <TableRow key={'staffRow_' + i}>
                        <ResponsiveTableCell>
                          {Object.prototype.hasOwnProperty.call(d, 'date')
                            ? ('0' + d.date[2]).slice(-2) + '/' + ('0' + d.date[1]).slice(-2) + '/' + d.date[0]
                            : '-'}
                          {formatStartTime(d.startTime)}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>
                          {Object.prototype.hasOwnProperty.call(d, 'executant') ? d['executant'] : '-'}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>
                          {Object.prototype.hasOwnProperty.call(d, 'qualification') ? d['qualification'] : '-'}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell align='right'>
                          {Object.prototype.hasOwnProperty.call(d, 'temps') && d.id !== -1 ? d['temps'] : t('7000014')}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>
                          {canUpdate && (
                            <Inline spacing='1-x'>
                              {Object.prototype.hasOwnProperty.call(d, 'temps') && d.id !== -1 && (
                                <>
                                  {LocalStorage.getOrDefault(
                                    [LocalStorage.KEY_PERMISSIONS, 'AMSWEB_ACCESS_MOMO-UPD'],
                                    LocalStorage.VALUE_TYPE.BOOLEAN,
                                    false,
                                  ) && (
                                    <IconButton size='small' variant='ghost' onClick={() => handleUpdateManHour(d)}>
                                      <FontAwesomeIcon size='xs' icon={faPen} />
                                    </IconButton>
                                  )}
                                  {LocalStorage.getOrDefault(
                                    [LocalStorage.KEY_PERMISSIONS, 'AMSWEB_ACCESS_MOMO-DLT'],
                                    LocalStorage.VALUE_TYPE.BOOLEAN,
                                    false,
                                  ) && (
                                    <IconButton
                                      size='small'
                                      variant='ghost'
                                      onClick={() => setManHoursToDelete({ ...d, open: true })}
                                    >
                                      {d?.id === manHoursToDelete?.id && manHoursToDelete?.loading ? (
                                        <Spinner size='small' />
                                      ) : (
                                        <FontAwesomeIcon size='xs' icon={faTrash} />
                                      )}
                                    </IconButton>
                                  )}
                                </>
                              )}
                            </Inline>
                          )}
                        </ResponsiveTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow />
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div style={{ paddingLeft: '1em' }}>{t('9500055')}</div>
          )}
        </div>
      </Collapsible>
      <ModalConfirm
        isOpened={Boolean(manHoursToDelete?.open)}
        onCloseModal={() => setManHoursToDelete(null)}
        confirmMessage={t('9510089')}
        onSubmit={handleDelete}
      />
      <ModalManHour
        lang={lang}
        open={isModalFormOpen}
        displayMode={modalFormDisplayMode}
        onClose={() => setIsModalFormOpen(false)}
        manHour={selectedResource}
        onPostSubmit={handleModalFormPostSubmit}
      />
    </div>
  ) : (
    ''
  )
}

export default WoManhour
